import React, { useEffect, useState } from 'react';
import { formatDate, formatToOptions, is12HourFormat } from '../../formatters';
import Popover from '../Popover';
import { useTranslation } from '../Translator';
import { useBlur } from '../../hooks/useBlur';
import { PickerButton } from '../PickerButton';
import { DateTimeSelector } from './DateTimeSelector';

const DateTimePicker = ({
  value,
  name,
  onChange,
  min,
  max,
  onBlur,
  disabled,
  placeholder,
  dataId,
  error,
  showClearActionButton,
  yearsSelectorRange,
} = {}) => {
  const [prevDateTime, setPrevDateTime] = useState(null);
  const [currDateTime, setCurrentDateTime] = useState(value);
  const { t, i18n } = useTranslation('components');
  const { ref, handleBlur } = useBlur({ name, value: currDateTime, onBlur, disabled });
  const zeroStateText = placeholder || t('selectTime');
  const displayValue = currDateTime
    ? formatDate(currDateTime, i18n.translator.language, formatToOptions('L LT'))
    : null;

  useEffect(() => {
    setCurrentDateTime(value);
  }, [value]);

  const handleChange = (event) => {
    onChange({
      target: {
        name,
        value: event.target.value,
      },
    });
  };

  return (
    <div
      ref={ref}
      data-ppuc-id={dataId}
    >
      <Popover
        position="bottom"
        activatorElement={(
          <PickerButton
            icon={{ type: 'icon', content: 'calendar' }}
            dataId={dataId}
            disabled={disabled}
            invalid={!!error}
          >
            {displayValue || zeroStateText}
          </PickerButton>
        )}
        onOpen={() => {
          setPrevDateTime(currDateTime);
        }}
        onClose={() => {
          setCurrentDateTime(prevDateTime);
          handleChange({ target: { value: prevDateTime } });
        }}
        disabled={disabled}
        dataId={`${dataId}.popover`}
        onBlur={handleBlur}
        speechBubble
        noPadding
      >
        {close => (
          <DateTimeSelector
            is12HourFormat={is12HourFormat(i18n.translator.language)}
            dataId={`${dataId}.selector`}
            value={value}
            min={min}
            max={max}
            yearsSelectorRange={yearsSelectorRange}
            popoverClose={close}
            currDateTime={currDateTime}
            prevDateTime={prevDateTime}
            setPrevDateTime={setPrevDateTime}
            setCurrentDateTime={setCurrentDateTime}
            showClearActionButton={showClearActionButton}
          />
        )}
      </Popover>
    </div>
  );
};

DateTimePicker.defaultProps = {
  dataId: 'DateTimePicker',
  min: new Date(31532400000),
  // 2104-12-31
  max: new Date(4260121200000),
  yearsSelectorRange: 10,
  onChange: () => {},
  onBlur: () => {},
};

export { DateTimePicker };
